import React, { Fragment, useEffect, useMemo, useState } from "react";
import { IUploadedFileDto } from "../../../../common/model/file-exchange-dto";
import { Button, Dialog, DialogActions, DialogTitle } from "@mui/material";
import { BtmFailedInspectionContent } from "./btm-failed-inspection-content";
import { BtmReturnInspectionProduct, BtmReturnInspectionStatus } from "../model/btm-return-inspection-product";
import { useEffectAsync } from "../../../../common/hooks/use-effect-async";
import { t } from "i18next";

interface IBtmBulkyFailedInspectionDialogProps {
    data: BtmReturnInspectionProduct[];
    status: BtmReturnInspectionStatus;
    onCompleted: (data: BtmReturnInspectionProduct[]) => Promise<void>;
    onClose: () => void;
}

export const BtmBulkyFailedInspectionDialog = (props: IBtmBulkyFailedInspectionDialogProps) => {
    if (!props.data) {
        props.onClose();

        return <Fragment />;
    }

    const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
    const [itemsList, setItemsList] = useState<BtmReturnInspectionProduct[]>(props.data);
    const [commentTextValue, setCommentTextValue] = useState<string>("");
    const [filesList, setFilesList] = useState<IUploadedFileDto[]>([]);

    const isFormCompleted = useMemo(() => filesList.length > 0 && commentTextValue !== "",
        [filesList, commentTextValue]);

    const handleInspectionComplete = (comment: string, files: IUploadedFileDto[]) => {
        setItemsList(prev => [...prev].map((item, i) =>
            i === activeItemIndex ? item.inspect(props.status, comment, files) : item));

        if (activeItemIndex !== itemsList.length - 1) {
            setActiveItemIndex(prev => prev + 1);
        }
    };

    useEffect(() => {
        setCommentTextValue(itemsList[activeItemIndex].comment ?? "");
        setFilesList(itemsList[activeItemIndex].files ?? []);
    }, [activeItemIndex]);

    useEffectAsync(async () => {
        if (itemsList.find(item => item.status === BtmReturnInspectionStatus.None) === undefined) {
            await props.onCompleted(itemsList);
            props.onClose();
        }
    }, [itemsList]);

    const isLastStep = useMemo(() => activeItemIndex === itemsList.length - 1, [activeItemIndex]);

    return <Dialog open={true} fullWidth classes={{ paper: "btm-failed-inspection-dialog" }}>
        <DialogTitle>
            {t("components.blueBrandReturns.page.details.bulkInspection.title", {
                currentStep: activeItemIndex + 1,
                stepsCount: itemsList.length,
            })}
        </DialogTitle>
        <BtmFailedInspectionContent data={itemsList[activeItemIndex]} status={props.status}
                                    commentTextValue={commentTextValue}
                                    onCommentValueChange={setCommentTextValue}
                                    filesList={filesList}
                                    onFilesListChange={setFilesList} />
        <DialogActions>
            <Button variant={"contained"} color={"inherit"} onClick={props.onClose}>
                {t("components.blueBrandReturns.page.details.bulkInspection.navigation.cancelButton")}
            </Button>
            {activeItemIndex !== 0 &&
                <Button variant={"contained"} color={"inherit"}
                        onClick={() => setActiveItemIndex(prev => prev - 1)}>
                    {t("components.blueBrandReturns.page.details.bulkInspection.navigation.previousButton")}
                </Button>}
            <Button variant={"contained"} color={"secondary"} disabled={!isFormCompleted}
                    onClick={() => handleInspectionComplete(commentTextValue, filesList)}>
                {isLastStep ?
                    t("components.blueBrandReturns.page.details.bulkInspection.navigation.finishButton") :
                    t("components.blueBrandReturns.page.details.bulkInspection.navigation.nextButton")}
            </Button>
        </DialogActions>
    </Dialog>;
};
